/**
 * Socket.io Client Configuration
 * WebSocket connection for real-time features
 */

import { io, Socket } from 'socket.io-client';
import { tokenManager } from './api.client';
import { tokenRefreshService, TOKEN_EVENTS } from './token-refresh.service';
import type {
  PresenceUpdate,
  TypingIndicator,
  NewMessageEvent,
  MessageEditedEvent,
  MessageDeletedEvent,
  MessageUpdatedEvent,
  ReactionEvent,
  PollUpdatedEvent,
  PollClosedEvent,
  Channel,
} from '@/types/api.types';

// Socket.io server URL - configured via Vite proxy in development
const SOCKET_URL = import.meta.env.VITE_SOCKET_URL || 'http://localhost:5050';

export type SocketConnectionState = 'connected' | 'disconnected' | 'reconnecting';

class SocketClient {
  private socket: Socket | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private tokenRefreshListener: ((event: Event) => void) | null = null;
  private connectCallbacks: Array<() => void> = [];
  private connectionState: SocketConnectionState = 'disconnected';
  private connectionStateCallbacks: Array<(state: SocketConnectionState) => void> = [];

  /**
   * Initialize and connect to Socket.io server
   */
  connect(): Socket {
    if (this.socket?.connected) {
      return this.socket;
    }

    const token = tokenManager.getAccessToken();

    if (!token) {
      throw new Error('No authentication token available for WebSocket connection');
    }

    // Validate token is not expired before connecting
    if (tokenRefreshService.isTokenExpired(token)) {
      console.error('[SocketClient] Access token is expired, cannot connect');
      throw new Error('Access token expired. Please refresh your session.');
    }

    console.log('[SocketClient] Connecting with fresh token');

    // Entering the connecting phase — surfaced to the UI reconnect banner
    this.setConnectionState('reconnecting');

    this.socket = io(SOCKET_URL, {
      auth: {
        token,
      },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: this.maxReconnectAttempts,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
    });

    this.setupEventListeners();
    this.setupTokenRefreshListener();

    return this.socket;
  }

  /**
   * Setup default event listeners
   */
  private setupEventListeners(): void {
    if (!this.socket) return;

    this.socket.on('connect', () => {
      console.log('✅ WebSocket connected:', this.socket?.id);
      this.reconnectAttempts = 0;
      this.setConnectionState('connected');
      // Re-register all application-level listeners (survives token refresh reconnection)
      this.connectCallbacks.forEach(cb => cb());
    });

    this.socket.on('disconnect', (reason) => {
      console.log('❌ WebSocket disconnected:', reason);
      // Manual disconnects are terminal; everything else auto-reconnects
      this.setConnectionState(
        reason === 'io client disconnect' ? 'disconnected' : 'reconnecting'
      );
    });

    this.socket.on('connect_error', (error) => {
      console.error('🚨 WebSocket connection error:', error);
      this.reconnectAttempts++;

      // Check if error is due to expired token
      if (error.message && error.message.includes('jwt expired')) {
        console.error('[SocketClient] JWT expired error detected');
        window.dispatchEvent(new CustomEvent(TOKEN_EVENTS.EXPIRED));
      }

      if (this.reconnectAttempts >= this.maxReconnectAttempts) {
        console.error('Max reconnect attempts reached. Please refresh the page.');
        this.setConnectionState('disconnected');
      } else {
        this.setConnectionState('reconnecting');
      }
    });

    this.socket.on('error', (error) => {
      console.error('🚨 WebSocket error:', error);
    });

    // Manager-level reconnection lifecycle (fires across new attempts)
    this.socket.io.on('reconnect_attempt', () => {
      this.setConnectionState('reconnecting');
    });
    this.socket.io.on('reconnect_failed', () => {
      this.setConnectionState('disconnected');
    });
  }

  /**
   * Current connection state for UI (banner, indicators).
   */
  getConnectionState(): SocketConnectionState {
    return this.connectionState;
  }

  /**
   * Subscribe to connection-state changes. Returns nothing; pair with
   * offConnectionStateChange for cleanup.
   */
  onConnectionStateChange(callback: (state: SocketConnectionState) => void): void {
    this.connectionStateCallbacks.push(callback);
  }

  /**
   * Remove a connection-state subscription.
   */
  offConnectionStateChange(callback: (state: SocketConnectionState) => void): void {
    this.connectionStateCallbacks = this.connectionStateCallbacks.filter(
      (cb) => cb !== callback
    );
  }

  private setConnectionState(state: SocketConnectionState): void {
    if (this.connectionState === state) return;
    this.connectionState = state;
    this.connectionStateCallbacks.forEach((cb) => cb(state));
  }

  /**
   * Setup listener for token refresh events
   */
  private setupTokenRefreshListener(): void {
    // Remove existing listener if any
    if (this.tokenRefreshListener) {
      window.removeEventListener(TOKEN_EVENTS.REFRESHED, this.tokenRefreshListener);
    }

    // Create new listener
    this.tokenRefreshListener = () => {
      console.log('[SocketClient] Token refreshed, reconnecting with new token...');

      // Disconnect current socket
      if (this.socket) {
        this.socket.disconnect();
        this.socket = null;
      }

      // Reconnect with new token
      try {
        this.connect();
        console.log('[SocketClient] Reconnected successfully with new token');
      } catch (error) {
        console.error('[SocketClient] Failed to reconnect after token refresh:', error);
      }
    };

    window.addEventListener(TOKEN_EVENTS.REFRESHED, this.tokenRefreshListener);
  }

  /**
   * Disconnect from Socket.io server
   */
  disconnect(): void {
    // Remove token refresh listener
    if (this.tokenRefreshListener) {
      window.removeEventListener(TOKEN_EVENTS.REFRESHED, this.tokenRefreshListener);
      this.tokenRefreshListener = null;
    }

    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }

    this.setConnectionState('disconnected');
  }

  /**
   * Get the current socket instance
   */
  getSocket(): Socket | null {
    return this.socket;
  }

  /**
   * Check if socket is connected
   */
  isConnected(): boolean {
    return this.socket?.connected ?? false;
  }

  /**
   * Register a callback that fires on every socket connect/reconnect.
   * Survives token refresh reconnections (new socket instances).
   */
  onConnect(callback: () => void): void {
    this.connectCallbacks.push(callback);
  }

  /**
   * Remove a previously registered connect callback.
   */
  offConnect(callback: () => void): void {
    this.connectCallbacks = this.connectCallbacks.filter(cb => cb !== callback);
  }

  /**
   * Remove all application-level event listeners from the current socket.
   * Call before re-registering to prevent listener accumulation on reconnect.
   */
  removeAllAppListeners(): void {
    if (!this.socket) return;
    const events = [
      'message:new', 'message:edited', 'message:deleted', 'message:updated',
      'channel:updated', 'channel:deleted',
      'typing:indicator',
      'reaction:update',
      'presence:update', 'presence:online-users', 'status:update',
      'mention:new',
      'notification:new', 'notification:unread-count',
    ];
    for (const event of events) {
      this.socket.removeAllListeners(event);
    }
  }

  // ========================================
  // Presence Events
  // ========================================

  /**
   * Update user presence status
   */
  updatePresence(status: 'online' | 'offline' | 'away'): void {
    this.socket?.emit('presence:update', { status });
  }

  /**
   * Listen for presence updates
   */
  onPresenceUpdate(callback: (data: PresenceUpdate) => void): void {
    this.socket?.on('presence:update', callback);
  }

  /**
   * Remove presence update listener
   */
  offPresenceUpdate(callback?: (data: PresenceUpdate) => void): void {
    this.socket?.off('presence:update', callback);
  }

  /**
   * Change user status (online/away/busy/offline)
   */
  changeStatus(status: 'online' | 'away' | 'busy' | 'offline'): void {
    this.socket?.emit('status:change', { status });
  }

  /**
   * Listen for status updates
   */
  onStatusUpdate(callback: (data: { userId: string; status: string; statusMessage?: string | null; timestamp: string }) => void): void {
    this.socket?.on('status:update', callback);
  }

  /**
   * Remove status update listener
   */
  offStatusUpdate(callback?: (data: { userId: string; status: string; statusMessage?: string | null; timestamp: string }) => void): void {
    this.socket?.off('status:update', callback);
  }

  // ========================================
  // Typing Indicator Events
  // ========================================

  /**
   * Send typing indicator
   */
  sendTyping(channelId?: string, dmThreadId?: string): void {
    this.socket?.emit('typing:start', { channelId, dmThreadId });
  }

  /**
   * Send stop typing indicator
   */
  sendStopTyping(channelId?: string, dmThreadId?: string): void {
    this.socket?.emit('typing:stop', { channelId, dmThreadId });
  }

  /**
   * Listen for typing indicators
   */
  onTyping(callback: (data: TypingIndicator) => void): void {
    this.socket?.on('typing:indicator', callback);
  }

  /**
   * Remove typing indicator listener
   */
  offTyping(callback?: (data: TypingIndicator) => void): void {
    this.socket?.off('typing:indicator', callback);
  }

  // ========================================
  // Message Events
  // ========================================

  /**
   * Join a channel room for real-time updates
   */
  joinChannel(channelId: string): void {
    console.log('📥 Joining channel:', channelId, 'Socket connected:', this.socket?.connected);
    this.socket?.emit('channel:join', { channelId });
  }

  /**
   * Leave a channel room
   */
  leaveChannel(channelId: string): void {
    this.socket?.emit('channel:leave', { channelId });
  }

  /**
   * Join a DM thread room for real-time updates
   */
  joinDMThread(dmThreadId: string): void {
    this.socket?.emit('dm:join', { dmThreadId });
  }

  /**
   * Leave a DM thread room
   */
  leaveDMThread(dmThreadId: string): void {
    this.socket?.emit('dm:leave', { dmThreadId });
  }

  /**
   * Listen for new messages
   */
  onNewMessage(callback: (data: NewMessageEvent) => void): void {
    console.log('🎧 Registering message:new listener. Socket:', this.socket?.connected);
    this.socket?.on('message:new', callback);
  }

  /**
   * Remove new message listener
   */
  offNewMessage(callback?: (data: NewMessageEvent) => void): void {
    this.socket?.off('message:new', callback);
  }

  /**
   * Listen for message edits
   */
  onMessageEdited(callback: (data: MessageEditedEvent) => void): void {
    this.socket?.on('message:edited', callback);
  }

  /**
   * Remove message edited listener
   */
  offMessageEdited(callback?: (data: MessageEditedEvent) => void): void {
    this.socket?.off('message:edited', callback);
  }

  /**
   * Listen for message deletions
   */
  onMessageDeleted(callback: (data: MessageDeletedEvent) => void): void {
    this.socket?.on('message:deleted', callback);
  }

  /**
   * Remove message deleted listener
   */
  offMessageDeleted(callback?: (data: MessageDeletedEvent) => void): void {
    this.socket?.off('message:deleted', callback);
  }

  /**
   * Listen for message updates (e.g., when attachments are added)
   */
  onMessageUpdated(callback: (data: MessageUpdatedEvent) => void): void {
    this.socket?.on('message:updated', callback);
  }

  /**
   * Remove message updated listener
   */
  offMessageUpdated(callback?: (data: MessageUpdatedEvent) => void): void {
    this.socket?.off('message:updated', callback);
  }

  // ========================================
  // Reaction Events
  // ========================================

  /**
   * Listen for reaction updates
   */
  onReaction(callback: (data: ReactionEvent) => void): void {
    this.socket?.on('reaction:update', callback);
  }

  /**
   * Remove reaction listener
   */
  offReaction(callback?: (data: ReactionEvent) => void): void {
    this.socket?.off('reaction:update', callback);
  }

  // ========================================
  // Channel Events
  // ========================================

  /**
   * Listen for channel updates
   */
  onChannelUpdated(callback: (data: { channel: Channel }) => void): void {
    this.socket?.on('channel:updated', callback);
  }

  /**
   * Remove channel updated listener
   */
  offChannelUpdated(callback?: (data: { channel: Channel }) => void): void {
    this.socket?.off('channel:updated', callback);
  }

  /**
   * Listen for channel deletions
   */
  onChannelDeleted(callback: (data: { channelId: string }) => void): void {
    this.socket?.on('channel:deleted', callback);
  }

  /**
   * Remove channel deleted listener
   */
  offChannelDeleted(callback?: (data: { channelId: string }) => void): void {
    this.socket?.off('channel:deleted', callback);
  }

  // ========================================
  // Poll Events
  // ========================================

  /**
   * Listen for poll updates
   */
  onPollUpdated(callback: (data: PollUpdatedEvent) => void): void {
    this.socket?.on('poll:updated', callback);
  }

  /**
   * Remove poll updated listener
   */
  offPollUpdated(callback?: (data: PollUpdatedEvent) => void): void {
    this.socket?.off('poll:updated', callback);
  }

  /**
   * Listen for poll closed events
   */
  onPollClosed(callback: (data: PollClosedEvent) => void): void {
    this.socket?.on('poll:closed', callback);
  }

  /**
   * Remove poll closed listener
   */
  offPollClosed(callback?: (data: PollClosedEvent) => void): void {
    this.socket?.off('poll:closed', callback);
  }

  // ========================================
  // Notification Events
  // ========================================

  /**
   * Listen for new notification events (from user:{userId} room)
   */
  onNotification(callback: (data: { notification: { title?: string; message?: string }; timestamp: string }) => void): void {
    this.socket?.on('notification:new', callback);
  }

  /**
   * Remove notification listener
   */
  offNotification(callback?: (data: { notification: { title?: string; message?: string }; timestamp: string }) => void): void {
    this.socket?.off('notification:new', callback);
  }

  /**
   * Listen for unread count updates (from user:{userId} room)
   */
  onUnreadCount(callback: (data: { unreadCount: number; timestamp: string }) => void): void {
    this.socket?.on('notification:unread-count', callback);
  }

  /**
   * Remove unread count listener
   */
  offUnreadCount(callback?: (data: { unreadCount: number; timestamp: string }) => void): void {
    this.socket?.off('notification:unread-count', callback);
  }

  // ========================================
  // Version Events
  // ========================================

  /**
   * Listen for current version on connect
   */
  onVersionCurrent(callback: (data: { version: string; timestamp: string }) => void): void {
    this.socket?.on('version:current', callback);
  }

  /**
   * Remove version current listener
   */
  offVersionCurrent(callback?: (data: { version: string; timestamp: string }) => void): void {
    this.socket?.off('version:current', callback);
  }

  /**
   * Listen for version update broadcasts
   */
  onVersionUpdate(callback: (data: { version: string; timestamp: string }) => void): void {
    this.socket?.on('version:update', callback);
  }

  /**
   * Remove version update listener
   */
  offVersionUpdate(callback?: (data: { version: string; timestamp: string }) => void): void {
    this.socket?.off('version:update', callback);
  }
}

// Export singleton instance
export const socketClient = new SocketClient();
export default socketClient;
